import {
    ACTION_FAIL_MESSAGE,
    ActionType,
    EVENT_ENABLED_DELETE_MESSAGE,
    EVENT_ENABLED_MODIFY_MESSAGE,
    FETCH_EVENT_VARIABLES_FAIL_MESSAGE,
    FETCH_EVENTS_FAIL_MESSAGE,
    FETCH_GATEWAY_FAIL_MESSAGE,
    HttpStatus,
    TypeOption,
} from "constant";
import { capitalize, get, sortBy } from "lodash";
import { useEffect, useState } from "react";
import { Breadcrumb, Button, Col, Container, Form, Row } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import {
    getAllRegistry,
    getRegistryGroups,
    getRegistryInfo,
} from "service/gatewayService";

import dltWhiteIcon from "assets/svg/delete-white.svg";
import HoverAuthorizeTooltip from "components/authorize/AuthorizeTooltip";
import ContentWrapper from "components/content-wrapper/ContentWrapper";
import DefaultModal from "components/modals/ModalTemplate";
import { useDisableGatewayEventConfirm } from "hooks/useDisableGatewayEventConfirm";
import { WalletAlertComponent } from "hooks/wallet";
import EventService from "service/eventService";
import OrganisationService from "service/organisationService";
import { canAccess } from "utils/authorize-action";
import { isValid, showWarning } from "utils/eventFunctions";
import {
    isFourWiredMotorControllerApp,
    isHttpSuccess,
    orgId,
} from "utils/functions";

import { faker } from "@faker-js/faker";
import Box from "@mui/material/Box";
import "assets/css/event.css";
import { TabControl } from "components/common/TabControl";
import { EventHistory } from "components/events/EventHistory/EventHistory";
import { EventName } from "components/events/EventDetail/EventName";

import { Variable } from "generated/models";
import { useCheckIfBasicPlan } from "hooks/useCheckIfBasicPlan";
import EventVariableService from "service/eventVariableService";
import { MemberDto, MemberStatus, ResponseDto } from "types";
import { Event } from "types/Event";
import { Group } from "types/Gateways";
import { getAPIError, showErrorAlert, showSuccessAlert } from "utils/alert";
import { ActionPanel } from "../../components/events/EventDetail/ActionPanel/ActionPanel";
import { ConditionPanel } from "../../components/events/EventDetail/ConditionPanel/ConditionPanel";
import { ActuatorCommandMode } from "types/Actuator";

export const getDisplayedActuatorMode = (configMode: ActuatorCommandMode) => {
    let configMode_ = "";

    if (configMode === ActuatorCommandMode.PWM) {
        configMode_ = configMode;
    } else {
        configMode_ = capitalize(configMode);
    }

    return configMode_;
};

// Returns true only for gateways that have onboarded info payload.
const hasGatewayInfo = (gateway: any) => Boolean(gateway?.info);

// Keeps only onboarded gateways in each gateway group.
const getGroupsWithOnboardedGateways = (groups: Group[]): Group[] => {
    return groups.map((group) => ({
        ...group,
        gateways: group.gateways.filter(hasGatewayInfo),
    }));
};

// Creates a synthetic warning action that is shown before the conflicting action.
const createWarningAction = (order: number) => ({
    uuid: faker.datatype.uuid(),
    type_: "WARNING",
    order: order - 0.5,
});

// Tracks delay accumulation and resets duplicate checks after 5 seconds.
const trackDelayWindow = (
    action: any,
    seenTimers: Set<string>,
    seenActuators: Set<string>,
    totalDelay: number
) => {
    if (action.type_ !== ActionType.DELAY) {
        return totalDelay;
    }

    const nextTotalDelay = totalDelay + action.resource.seconds;
    if (nextTotalDelay >= 5) {
        seenTimers.clear();
        seenActuators.clear();
    }

    return nextTotalDelay;
};

// Adds a warning when a timer variable is repeated without enough delay.
const trackTimerVariableWarning = (
    action: any,
    eventVariablesMap: Map<string, Variable>,
    seenTimers: Set<string>,
    actionWithWarning: any[],
    totalDelay: number
) => {
    if (action.type_ !== ActionType.VARIABLE) {
        return totalDelay;
    }

    const variable = eventVariablesMap.get(action.resource.variable);
    if (variable?.type_ !== TypeOption.Timer) {
        return totalDelay;
    }

    if (seenTimers.has(variable.uuid)) {
        actionWithWarning.push(createWarningAction(action.order));
    }
    seenTimers.add(variable.uuid);
    return 0;
};

// Adds a warning when the same actuator command appears repeatedly.
const trackActuatorWarning = (
    action: any,
    seenActuators: Set<string>,
    actionWithWarning: any[],
    totalDelay: number
) => {
    if (action.type_ !== ActionType.ACTUATOR) {
        return totalDelay;
    }

    const actuatorKey = `${action.resource?.gateway_id}|${action.resource?.device_id}|${action.resource?.said}`;
    if (seenActuators.has(actuatorKey)) {
        actionWithWarning.push(createWarningAction(action.order));
    }

    seenActuators.add(actuatorKey);
    return 0;
};

const EventDetails = () => {
    const [eventVariables, setEventVariables] = useState([]);

    const [orgSelectionList, setOrgSelectionList] = useState<Array<MemberDto>>(
        []
    );

    const [toDelete, setToDelete] = useState({
        type: "",
        id: "",
    });
    const params: any = useParams();
    const [eventDetail, setEventDetail] = useState<Event>();
    const [initialGatewayGroupList, setInitialGatewayGroupList] = useState<
        Group[]
    >([]);
    const [allGateways, setAllGateways] = useState([]);

    const [actionList, setActionList] = useState<any[]>([]);
    const history = useHistory();

    const [modalShow, setModalShow] = useState(false);
    const [modalType, setModalType] = useState("");
    const [modalContent, setModalContent] = useState("");
    const { confirmIfNeeded, disableConfirmModal } =
        useDisableGatewayEventConfirm();

    const [isPageLoading, setIsPageLoading] = useState(true);
    const [eventActive, setEventActive] = useState(false);
    const [isForbiddenResource, setIsForbiddenResource] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const { IS_BASIC_PLAN } = useCheckIfBasicPlan();

    const [selectedTab, setSelectedTab] = useState("condition_actions");

    const ACTION = "action";
    const CONDITION = "condition";
    const EVENT = "event";

    useEffect(() => {
        const initializeData = async () => {
            try {
                setIsLoaded(false);
                const [
                    eventVariablesResp,
                    allGatewaysResp,
                    gatewayGroupListResp,
                    orgMembersResp,
                ] = await Promise.all([
                    EventVariableService.readEventVariables(),
                    getAllRegistry(),
                    getRegistryGroups(),
                    OrganisationService.getOrgMembers(orgId()),
                ]);

                const responseStatus = [
                    eventVariablesResp.status,
                    allGatewaysResp.status,
                    gatewayGroupListResp.status,
                    orgMembersResp.status,
                ];

                // If any of the responses are forbidden, set the forbidden resource to true and return
                if (responseStatus.includes(HttpStatus.FORBIDDEN)) {
                    setIsForbiddenResource(true);

                    return;
                }

                // If all of the responses are successful, set the data to the state

                if (responseStatus.every(isHttpSuccess)) {
                    // Set Event Variables
                    setEventVariables(eventVariablesResp.data);

                    // Set All Gateways
                    const { data: gateways = [] } = allGatewaysResp;
                    const onBoardedGateways = gateways.filter(hasGatewayInfo);
                    setAllGateways(onBoardedGateways);

                    // Set Gateway Group List
                    const { data: groups } = gatewayGroupListResp;
                    const groupsWithOnboardedGateways: Group[] =
                        getGroupsWithOnboardedGateways(groups);
                    setInitialGatewayGroupList(groupsWithOnboardedGateways);

                    // Set Organization Members
                    const { data: orgMembers = [] } = orgMembersResp;
                    const orgMembersList = orgMembers
                        .toCamelCase<MemberDto>()
                        .filter((curr) => curr.status !== MemberStatus.Pending);
                    setOrgSelectionList(orgMembersList);
                    setIsLoaded(true);
                    return;
                }

                if (!isHttpSuccess(eventVariablesResp.status)) {
                    showErrorAlert(
                        getAPIError(
                            eventVariablesResp,
                            FETCH_EVENT_VARIABLES_FAIL_MESSAGE
                        )
                    );
                } else if (
                    !isHttpSuccess(allGatewaysResp.status) ||
                    !isHttpSuccess(gatewayGroupListResp.status)
                ) {
                    showErrorAlert(
                        getAPIError(allGatewaysResp, FETCH_GATEWAY_FAIL_MESSAGE)
                    );
                }

                showErrorAlert({
                    message: "An error occurred while fetching data",
                });
            } catch {
                showErrorAlert({
                    message: "An error occurred while fetching data",
                });
            }
        };

        initializeData();
    }, []);

    const sortAndUpdateAction = async (actions: any[] = []) => {
        const sortedActions = sortBy(
            actions.filter((action) => action.type_ !== "WARNING"),
            "order"
        );
        const { data }: ResponseDto<Variable[]> =
            await EventVariableService.readEventVariables();
        const eventVariablesMap = new Map(
            data?.map((item: Variable) => [item.uuid, item]) ?? []
        );

        let seenTimers = new Set<string>();
        const actionWithWarning: any[] = [];
        let totalDelay = 0;
        let seenActuators = new Set<string>();
        for (const action of sortedActions) {

            // check delay -> clear timerId
            // work for if (action.type_ === ActionType.DELAY)
            // Tracks delay accumulation and resets duplicate checks after 5 seconds.
            totalDelay = trackDelayWindow(
                action,
                seenTimers,
                seenActuators,
                totalDelay
            );

            // check Timer variable -> add warning if need
            // work for if (action.type_ === ActionType.VARIABLE)
            // Adds a warning when a timer variable is repeated without enough delay.
            totalDelay = trackTimerVariableWarning(
                action,
                eventVariablesMap,
                seenTimers,
                actionWithWarning,
                totalDelay
            );

            // check Actuator -> add warning if need
            // work for if (action.type_ === ActionType.ACTUATOR)
            // Adds a warning when the same actuator command appears repeatedly.
            totalDelay = trackActuatorWarning(
                action,
                seenActuators,
                actionWithWarning,
                totalDelay
            );

            // Marks actuator action as four-wire app when registry metadata matches.
            if (action.resource?.gateway_id) {
                const res = await getRegistryInfo(action.resource?.gateway_id);
                const device = get(res, "data.devices", []).find(
                    (device: any) => device.UID === action.resource?.device_id
                );
                const app = device?.APP?.[action.resource?.said];

                if (isHttpSuccess(res.status) && device && app) {
                    const isFourWired = isFourWiredMotorControllerApp(
                        device.OBJ,
                        app.APPID
                    );

                    action.fourWireApp = isFourWired;
                }
            }

            actionWithWarning.push(action);
        }
        setActionList(actionWithWarning);
    };

    const updateEventState = async ({ data }: any) => {
        const { actions = [], activate = false } = data;

        setEventDetail(data);

        // for actions
        await sortAndUpdateAction(actions);

        // De-active edit event when event is active
        setEventActive(activate);
    };

    const fetchEvent = async () => {
        const eventResp = await EventService.readOneEvent(params.eventId);

        if (eventResp.status === HttpStatus.FORBIDDEN) {
            setIsForbiddenResource(true);
        } else if (isHttpSuccess(eventResp.status)) {
            await updateEventState(eventResp);
        } else {
            showErrorAlert(getAPIError(eventResp, FETCH_EVENTS_FAIL_MESSAGE));
        }

        setIsPageLoading(false);
    };

    useEffect(() => {
        if (canAccess("event:read")) {
            fetchEvent();
        } else {
            setIsPageLoading(false);
            setIsForbiddenResource(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.eventId, globalThis.window.authorizationConfig?.event]);

    const showDeleteConfirmation = (type: string, uuid: string) => {
        if (eventActive) {
            return showErrorAlert({
                message: EVENT_ENABLED_MODIFY_MESSAGE,
            });
        }

        setToDelete({
            type: type,
            id: uuid,
        });
        showDeleteModal(type);
    };

    const showDeleteEventConfirmation = () => {
        if (eventActive) {
            return showErrorAlert({
                message: EVENT_ENABLED_DELETE_MESSAGE,
            });
        }

        setToDelete({
            ...toDelete,
            type: EVENT,
        });
        showDeleteModal(EVENT);
    };

    const showDeleteModal = (type: string) => {
        setModalShow(true);
        setModalType("dlt");
        setModalContent(`Do you want to delete this ${type}?`);
    };

    const performToggleEventStatus = async (
        event_id: string,
        active: boolean
    ) => {
        const eventUpdateResp = await EventService.updateEvent(event_id, {
            activate: !active,
        });

        if (isHttpSuccess(eventUpdateResp.status)) {
            showSuccessAlert({
                title: "success",
                message: `Event has been ${active ? "disabled" : "enabled"
                    } successfully.`,
            });
            setEventActive(!eventActive);
            eventDetail &&
                setEventDetail({ ...eventDetail, activate: !active });
        } else {
            showErrorAlert(getAPIError(eventUpdateResp, ACTION_FAIL_MESSAGE));
        }
    };

    const toggleEventStatus = (event_id: string, active: boolean) => {
        // call get Event API

        if (!isValid(eventDetail, allGateways)) {
            return showWarning();
        }

        confirmIfNeeded({ activate: active, gateway_id: eventDetail?.gateway_id }, () =>
            performToggleEventStatus(event_id, active)
        );
    };

    const deleteCondition = async () => {
        const response = await EventService.deleteCondition(
            params.eventId,
            toDelete.id
        );

        if (isHttpSuccess(response.status)) {
            setModalShow(false);
            showSuccessAlert({
                message: "Condition has been deleted successfully.",
            });

            fetchEvent().then(() => {
                const element = document.getElementById("gs-" + toDelete.id);
                const elementGrid = element?.parentNode as any;
                elementGrid?.gridstack?.removeWidget(element);
            });
        } else {
            showErrorAlert(getAPIError(response, ACTION_FAIL_MESSAGE));
        }
    };

    const deleteAction = async () => {
        const response = await EventService.deleteAction(
            params.eventId,
            toDelete.id
        );

        if (isHttpSuccess(response.status)) {
            setModalShow(false);
            showSuccessAlert({
                message: "Action has been deleted successfully.",
            });

            const clone = [...actionList];
            const filtered = clone.filter((action: any) => {
                return action.uuid !== toDelete.id;
            });
            await sortAndUpdateAction(filtered);
        } else {
            showErrorAlert(getAPIError(response, ACTION_FAIL_MESSAGE));
        }
    };

    const deleteEvent = async () => {
        const response = await EventService.deleteEvent(params.eventId);
        if (isHttpSuccess(response.status)) {
            showSuccessAlert({
                message: `Event has been deleted successfully.`,
            });
            setModalShow(false);
            history.push(`/events`);
        } else {
            showErrorAlert(getAPIError(response, ACTION_FAIL_MESSAGE));
        }
    };

    const handleTabChange = (
        _event: React.SyntheticEvent,
        newValue: string
    ) => {
        setSelectedTab(newValue);
    };

    return (
        <ContentWrapper
            isLoading={isPageLoading}
            isForbiddenResource={isForbiddenResource}
        >
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col xs="6">
                            <h5 className="page-title overflow-text">
                                {eventDetail?.name}
                            </h5>
                        </Col>
                        <Col xs="6" className="text-right">
                            <HoverAuthorizeTooltip permission="event:delete">
                                <Button
                                    variant="danger"
                                    onClick={showDeleteEventConfirmation}
                                >
                                    <img
                                        src={dltWhiteIcon}
                                        alt=""
                                        style={{ marginRight: "8px" }}
                                    />{" "}
                                    Delete
                                </Button>
                            </HoverAuthorizeTooltip>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs="12">
                            <Breadcrumb className="w-100">
                                <Breadcrumb.Item href="/events">
                                    Events
                                </Breadcrumb.Item>
                                <Breadcrumb.Item active>
                                    Event Details
                                </Breadcrumb.Item>
                            </Breadcrumb>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="8" lg="7" xl="6">
                            {eventDetail && (
                                <EventName
                                    eventName={eventDetail.name}
                                    isActive={eventDetail.activate}
                                    eventUUID={eventDetail.uuid}
                                    gateway_id={eventDetail.gateway_id}
                                    setEventDetail={setEventDetail}
                                />
                            )}
                        </Col>
                        <Col md="4" lg="5" xl="6">
                            <div className="form-box enable-event-box">
                                <span>Enable this event</span>
                                <HoverAuthorizeTooltip permission="event:update">
                                    <div>
                                        <Form.Check
                                            type="switch"
                                            id={`switch_${params.eventId}`}
                                            label=""
                                            checked={eventActive}
                                            disabled={
                                                !canAccess("event:update") ||
                                                (eventDetail?.gateway_id &&
                                                    eventDetail?.conditions
                                                        .length <= 0 &&
                                                    eventDetail?.actions
                                                        .length <= 0) ||
                                                false
                                            }
                                            onChange={() => {
                                                toggleEventStatus(
                                                    params.eventId,
                                                    eventActive
                                                );
                                            }}
                                        />
                                    </div>
                                </HoverAuthorizeTooltip>
                            </div>
                        </Col>
                    </Row>

                    <Row className="my-3">
                        <Col sm={12}>
                            {!IS_BASIC_PLAN && (
                                <WalletAlertComponent showWarningWhen="LowBalance" />
                            )}
                        </Col>
                    </Row>

                    <TabControl
                        value={selectedTab}
                        onChange={handleTabChange}
                        tabs={[
                            {
                                value: "condition_actions",
                                label: (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            fontSize: "20px",
                                            padding: "15px",
                                        }}
                                    >
                                        Conditions and Actions
                                    </Box>
                                ),
                            },
                            {
                                value: "history",
                                label: (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            fontSize: "20px",
                                            padding: "15px",
                                        }}
                                    >
                                        Activation History
                                    </Box>
                                ),
                            },
                        ]}
                    />

                    {selectedTab === "condition_actions" && (
                        <>
                            <Row className="mb-2">
                                <Col xs="12">
                                    <div className="form-box condition-note-box">
                                        <span>Note: </span>
                                        <span className="note-text">
                                            Event conditions or actions with a
                                            duration/delay shorter than the
                                            report rate interval will not
                                            function as expected.
                                        </span>
                                    </div>
                                </Col>
                            </Row>

                            {isLoaded && (
                                <Row className="conditions-actions">
                                    <Col lg="6">
                                        <div className="when-do-title">
                                            When
                                        </div>

                                        {/* Show condition  */}
                                        {eventDetail && (
                                            <ConditionPanel
                                                event={eventDetail}
                                                initialGatewayGroupList={
                                                    initialGatewayGroupList
                                                }
                                                eventVariables={eventVariables}
                                                allGateways={allGateways}
                                                showDeleteConfirmation={
                                                    showDeleteConfirmation
                                                }
                                            />
                                        )}
                                        <div className="divider"></div>
                                    </Col>
                                    <Col lg="6">
                                        <div className="arrow-circle">
                                            <i className="material-icons right">
                                                keyboard_arrow_right
                                            </i>
                                        </div>

                                        <div className="when-do-title">Do</div>
                                        {eventDetail && (
                                            <ActionPanel
                                                event={eventDetail}
                                                actionList={actionList}
                                                eventVariables={eventVariables}
                                                allGateways={allGateways}
                                                orgMemberList={orgSelectionList}
                                                showDeleteConfirmation={
                                                    showDeleteConfirmation
                                                }
                                                fetchEvent={fetchEvent}
                                                sortAndUpdateAction={
                                                    sortAndUpdateAction
                                                }
                                            />
                                        )}
                                    </Col>
                                </Row>
                            )}
                        </>
                    )}

                    {selectedTab === "history" && eventDetail && (
                        <EventHistory event={eventDetail} />
                    )}
                </Container>
            </div>

            <DefaultModal
                modalShow={modalShow}
                setModalShow={setModalShow}
                modalType={modalType}
                modalContent={modalContent}
                okAction={() => {
                    if (toDelete.type === EVENT) {
                        history.push(`/events`);
                    } else {
                        setModalShow(false);
                    }
                }}
                deleteFunction={() => {
                    if (toDelete.type === CONDITION) {
                        deleteCondition();
                    } else if (toDelete.type === ACTION) {
                        deleteAction();
                    } else if (toDelete.type === EVENT) {
                        deleteEvent();
                    }
                }}
            />

            {disableConfirmModal}
        </ContentWrapper>
    );
};

export default EventDetails;
